import { z } from "zod";
import { CONTENT_BLOB_VERSION, type ContentBlob } from "../codec.js";

/**
 * Content schemas — the shapes that live *inside* a ContentBlob.
 *
 * The rule is deliberately mechanical so nobody has to make a judgement call per
 * field: **any text a person typed is content**. Everything else — ids,
 * timestamps, flags, ordering — is metadata the server needs in order to sort,
 * sync and remind, and stays readable in both encryption modes (SPEC §3).
 *
 * `user.displayName` is the one deliberate exception. A person belongs to several
 * spaces, so it cannot be encrypted under any single space key. SPEC §3 already
 * accepts that the server knows who acted and when.
 */

export const spaceContentSchema = z.object({
  name: z.string().trim().min(1).max(120),
});
export type SpaceContent = z.infer<typeof spaceContentSchema>;

/** What a space is called when whoever created it did not say. */
export const DEFAULT_SPACE_NAME = "Наше пространство";

/**
 * A brand-new space's content blob.
 *
 * Two places create a space nobody has typed a name for yet — the bootstrap CLI
 * and registration by an app-wide invite code — and both need the same shape:
 * plaintext mode, where a blob simply carries the JSON. Written once here so
 * that the default name and the blob layout cannot drift apart.
 */
export function plainSpaceContent(name?: string | null): ContentBlob {
  const trimmed = name?.trim();

  return {
    v: CONTENT_BLOB_VERSION,
    mode: "none",
    data: { name: trimmed || DEFAULT_SPACE_NAME } satisfies SpaceContent,
  };
}

export const listContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  icon: z.string().max(32).optional(),
});
export type ListContent = z.infer<typeof listContentSchema>;

export const listItemContentSchema = z.object({
  text: z.string().trim().min(1).max(2_000),
  note: z.string().max(10_000).optional(),
});
export type ListItemContent = z.infer<typeof listItemContentSchema>;

/**
 * A wish's content (SPEC §4.3).
 *
 * `url` is optional by design. Everything here is inside the blob, including the
 * unfurled preview: the server fetches it but stores it back as content it
 * cannot read, so an e2ee space keeps its previews too.
 */
export const wishContentSchema = z.object({
  title: z.string().trim().min(1).max(300),
  url: z.url().max(2_000).optional(),
  note: z.string().max(10_000).optional(),
  price: z.string().max(60).optional(),
  /**
   * What the server read off the page, folded in by the client.
   *
   * Inside the blob rather than beside it, so an encrypted space keeps its
   * previews: the server fetched the page but cannot see what was saved.
   */
  preview: z
    .object({
      title: z.string().max(300).optional(),
      description: z.string().max(1_000).optional(),
      /** A path on our own origin. Never the shop's address — see UnfurlResult. */
      image: z.string().max(300).optional(),
      siteName: z.string().max(120).optional(),
      fetchedAt: z.iso.datetime().optional(),
    })
    .optional(),
});
export type WishContent = z.infer<typeof wishContentSchema>;

export const topicContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
});
export type TopicContent = z.infer<typeof topicContentSchema>;

export const messageContentSchema = z.object({
  /**
   * May be empty, because a photograph is a message.
   *
   * The minimum used to be one character, which made a picture with no caption
   * impossible to send — a rule about rows enforced on somebody who just wanted
   * to show something. What refuses a message that is neither words nor files
   * is the composer, which is the only place that can see both.
   */
  body: z.string().trim().max(10_000),
});
export type MessageContent = z.infer<typeof messageContentSchema>;

export const eventContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  note: z.string().max(10_000).optional(),
  location: z.string().max(300).optional(),
  /**
   * A link the event is about — a call, a ticket, a map.
   *
   * Inside the content blob rather than a column, like every other thing a
   * person typed: the server has no business reading where a couple is meeting,
   * and in an encrypted space it could not anyway.
   *
   * Not an attachment: an attachment is bytes this server stores, and a
   * conference link is a string that belongs to somebody else's service.
   */
  url: z.string().trim().url().max(2000).optional(),
});
export type EventContent = z.infer<typeof eventContentSchema>;

/**
 * A place on the map (SPEC §4.10).
 *
 * The coordinates are in here, and that is the one thing in this file that is
 * not text somebody typed. The rule the rest of the file follows — content is
 * what a person wrote, metadata is what the server needs to sort, sync and
 * remind — gives the same answer anyway: nothing on the server needs to know
 * where a couple eats. Proximity is worked out on the phone, because the
 * platform gives it to nobody else (§4.10), so a latitude in a column would be
 * a leak bought for nothing.
 *
 * Precision is not clamped. Six decimal places is a doorway and two is a
 * district, and which of those somebody wants is their business.
 */
export const placeContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  /** As written or as the search returned it — never as the server derived it. */
  address: z.string().max(300).optional(),
  note: z.string().max(10_000).optional(),
  /** A page about the place: the restaurant's own, or a map link. */
  url: z.url().max(2_000).optional(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type PlaceContent = z.infer<typeof placeContentSchema>;

/**
 * What one person wrote about a place they have been.
 *
 * Only the words. The score beside them is a number out of five that the server
 * keeps in a column — see `placeReviewSchema` for why that one is not a secret
 * worth keeping and this one is.
 */
export const placeReviewContentSchema = z.object({
  text: z.string().trim().max(4_000),
});
export type PlaceReviewContent = z.infer<typeof placeReviewContentSchema>;

export const tagContentSchema = z.object({
  name: z.string().trim().min(1).max(60),
});
export type TagContent = z.infer<typeof tagContentSchema>;

/**
 * What a person named a file, and anything they said about it.
 *
 * Inside a blob like every other piece of typed text — the bytes themselves are
 * stored separately and, in an encrypted space, will be encrypted before they
 * ever leave the device.
 */
export const attachmentContentSchema = z.object({
  name: z.string().trim().min(1).max(200),
  caption: z.string().max(1_000).optional(),
});
export type AttachmentContent = z.infer<typeof attachmentContentSchema>;

/**
 * The short summary that rides inside a push payload (SPEC §3, decision 3).
 *
 * Written and encrypted by the client that performed the action, forwarded by
 * the server untouched, decrypted by the service worker. Kept small because a
 * Web Push payload caps at 4KB after encryption and base64 inflates by a third.
 */
export const notificationPreviewSchema = z.object({
  title: z.string().max(120),
  body: z.string().max(240).optional(),
});
export type NotificationPreview = z.infer<typeof notificationPreviewSchema>;
